// The images a guide can use (":::figure <id>" and heroImage in front matter), each as WebP at several widths so
// the browser picks the smallest that's sharp enough.
//
//  - find-*: real screenshots of photohawk-staging.com/find-your-photos (the demo workspace's galleries), captured
//    with Playwright (Oct 2026). No photo of a person is in them.
//  - uploader, uploader-*: the real web uploader with demo data (src/assets/web-uploader, credits in its CREDITS.md).
//  - runners: a public-domain race photo (src/assets/home/CREDITS.md, photo 7).
//  - split-*, invited-*: real components (the gallery's Revenue split card, the payouts card, Earnings, the sign-in page
//    and the web uploader) rendered with demo data and mocked APIs by scripts/guide-shots (shoot.mjs, encode.py).
// Made from the full-size captures with ImageMagick: `convert in.png -strip -resize <w>x -quality 80 out-<w>.webp`.

import runner400 from "@/assets/home/run-3-mile-400.webp";
import runner800 from "@/assets/home/run-3-mile-800.webp";
import runnerFull from "@/assets/home/run-3-mile-full.webp";
import uploader1x from "@/assets/web-uploader/uploader-desktop-1x.webp";
import uploader2x from "@/assets/web-uploader/uploader-desktop-2x.webp";
import status1x from "@/assets/web-uploader/status-1x.webp";
import status2x from "@/assets/web-uploader/status-2x.webp";
import resume1x from "@/assets/web-uploader/resume-1x.webp";
import resume2x from "@/assets/web-uploader/resume-2x.webp";
import visitors1x from "@/assets/web-uploader/live-visitors-1x.webp";
import visitors2x from "@/assets/web-uploader/live-visitors-2x.webp";
import { MEDIA, type MediaImage } from "@/content/media";

export interface GuideImage {
  /** The default (mid-size) file. */
  src: string;
  /** "url 640w, url 960w, …" */
  srcSet: string;
  /** Intrinsic size of the largest variant (for width/height, so nothing shifts as it loads). */
  width: number;
  height: number;
  /** Default `sizes` when the figure doesn't set one. */
  sizes?: string;
  /** Shown no wider than this (CSS px), for small screenshots that would look blown up across the column. */
  maxWidth?: number;
  /** Modern formats offered ahead of `src`/`srcSet` (a <picture>): the content images' AVIF and WebP. */
  sources?: { type: string; srcSet: string }[];
}

/** A content image (src/content/media) as a guide image: AVIF and WebP sources, with JPEG as the fallback. */
export const mediaGuideImage = (m: MediaImage): GuideImage => ({
  src: m.src,
  srcSet: m.srcSet.jpg,
  width: m.width,
  height: m.height,
  sources: [
    { type: "image/avif", srcSet: m.srcSet.avif },
    { type: "image/webp", srcSet: m.srcSet.webp },
  ],
});

const GUIDE_FILES = import.meta.glob("/src/assets/guides/*.webp", { eager: true, import: "default" }) as Record<string, string>;

/** An image made of src/assets/guides/<id>-<width>.webp files. */
const variants = (id: string, aspect: number, extra: [string, number][] = []): GuideImage => {
  const found: [string, number][] = Object.entries(GUIDE_FILES).flatMap(([path, url]) => {
    const m = new RegExp(`/${id}-(\\d+)\\.webp$`).exec(path);
    return m ? [[url, Number(m[1])] as [string, number]] : [];
  });
  const all = [...found, ...extra].sort((a, b) => a[1] - b[1]);
  if (!all.length) throw new Error(`no files for guide image ${id}`);
  const largest = all[all.length - 1][1];
  const mid = all.find(([, w]) => w >= 960) ?? all[all.length - 1];
  return { src: mid[0], srcSet: all.map(([u, w]) => `${u} ${w}w`).join(", "), width: largest, height: Math.round(largest * aspect) };
};

const fixed = (files: [string, number][], width: number, height: number, sizes?: string, maxWidth?: number): GuideImage => ({
  src: files[0][0],
  srcSet: files.map(([u, w]) => `${u} ${w}w`).join(", "),
  width,
  height,
  sizes,
  maxWidth,
});

export const GUIDE_IMAGES: Record<string, GuideImage> = {
  "find-browse": variants("find-browse", 1800 / 2880),
  "find-wizard": variants("find-wizard", 1800 / 2880),
  "find-dialog": { ...variants("find-dialog", 652 / 1024), sizes: "(min-width: 768px) 512px, 100vw", maxWidth: 512 },
  uploader: variants("uploader", 750 / 1200, [
    [uploader1x, 1200],
    [uploader2x, 2400],
  ]),
  "uploader-status": fixed([[status1x, 399], [status2x, 798]], 798, 660, "(min-width: 768px) 400px, 100vw", 400),
  "uploader-resume": fixed([[resume1x, 960], [resume2x, 1920]], 1920, 660),
  "uploader-visitors": fixed([[visitors1x, 720], [visitors2x, 1440]], 1440, 460),
  // Revenue split and invited-photographer guides: real components with demo data (scripts/guide-shots).
  "split-settings": { ...variants("split-settings", 3528 / 1792), sizes: "(min-width: 768px) 640px, 100vw", maxWidth: 640 },
  "split-empty": { ...variants("split-empty", 1624 / 1792), sizes: "(min-width: 768px) 640px, 100vw", maxWidth: 640 },
  "split-list": variants("split-list", 730 / 1660),
  "split-cases": variants("split-cases", 624 / 1660),
  "split-over": variants("split-over", 672 / 1660),
  "split-add-group": { ...variants("split-add-group", 1952 / 896), sizes: "(min-width: 768px) 360px, 100vw", maxWidth: 360 },
  "split-payouts": variants("split-payouts", 864 / 1792),
  "split-earnings": { ...variants("split-earnings", 1602 / 1536), sizes: "(min-width: 768px) 600px, 100vw", maxWidth: 600 },
  "split-payee": { ...variants("split-payee", 2122 / 1640), sizes: "(min-width: 768px) 600px, 100vw", maxWidth: 600 },
  "split-your-photos": { ...variants("split-your-photos", 1238 / 1536), sizes: "(min-width: 768px) 600px, 100vw", maxWidth: 600 },
  "split-own-photos": { ...variants("split-own-photos", 404 / 1536), sizes: "(min-width: 768px) 600px, 100vw", maxWidth: 600 },
  "invited-signin": { ...variants("invited-signin", 1280 / 1200), sizes: "(min-width: 768px) 420px, 100vw", maxWidth: 420 },
  "invited-workspaces": variants("invited-workspaces", 1200 / 2400),
  "invited-galleries": variants("invited-galleries", 1200 / 2400),
  "invited-collection": variants("invited-collection", 1200 / 2400),
  runners: fixed([[runner800, 800], [runner400, 400], [runnerFull, 1023]], 1023, 684),
  // The content images (screenshots and diagrams), by name.
  ...Object.fromEntries(Object.values(MEDIA).map((m) => [m.name, mediaGuideImage(m)])),
};
